import { useCallback, useMemo, useState } from 'react'
import { Gavel, ClipboardCheck, Users } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { ModListScreen } from './ModListScreen'
import {
  makeFetcher,
  submitDecision,
  giveSecondOpinion,
  MOD_STR as S,
  type ReviewRow,
  type DecisionRow,
  type SecondOpinionRow,
} from './modContentApi'

const TARGET_TYPES = ['message', 'user', 'group', 'channel', 'bot', 'media'] as const
const DECISION_ACTIONS = ['approve', 'remove', 'warn', 'restrict', 'ban', 'dismiss'] as const

/**
 * A6.19 — Qaror formasi (tugmalar + sabab). Moderator obyektga qaror qabul qiladi
 * (POST /admin/moderation/decisions) — sabab MAJBURIY (audit izi). Pastda so'nggi
 * qarorlar jurnali (GET /admin/moderation/decisions).
 */
export function DecisionFormScreen() {
  const { toast } = useToast()
  const [targetType, setTargetType] = useState<string>('message')
  const [targetId, setTargetId] = useState('')
  const [action, setAction] = useState<string>('remove')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const submit = useCallback(async () => {
    const id = Number(targetId.trim())
    if (!Number.isFinite(id) || id <= 0 || !reason.trim()) {
      toast(S.needTarget, { tone: 'warning' })
      return
    }
    setBusy(true)
    try {
      await submitDecision({ target_type: targetType, target_id: id, action, reason: reason.trim() })
      toast(S.decisionSaved, { tone: 'success' })
      setTargetId('')
      setReason('')
      setReloadKey((k) => k + 1)
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }, [targetType, targetId, action, reason, toast])

  const columns = useMemo<AdminColumn<DecisionRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id' },
      { key: 'target_type', header: S.targetType, type: 'text' },
      { key: 'target_id', header: S.targetId, type: 'number' },
      { key: 'action', header: S.action, type: 'text' },
      { key: 'reason', header: S.reason, type: 'longtext' },
      { key: 'moderator', header: S.moderator, type: 'text' },
      { key: 'created_at', header: S.created, type: 'date' },
    ],
    [],
  )
  const fetcher = useMemo(
    () =>
      makeFetcher<DecisionRow>(
        '/admin/moderation/decisions',
        'items',
        (r) => `${r.target_type} ${r.action} ${r.moderator}`,
      ),
    // reloadKey changes the identity → DataTable refetches after a new decision.
    [reloadKey],
  )

  const fieldCls =
    'rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-primary'

  return (
    <>
      <PageHeader title={S.decision} screenId="A6.19" description={S.decisionDesc} />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        <div className="max-w-3xl space-y-2.5 rounded-lg border border-border-default bg-surface-2 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <select value={targetType} onChange={(e) => setTargetType(e.target.value)} className={fieldCls}>
              {TARGET_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <input
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              placeholder={S.targetId}
              inputMode="numeric"
              className={`${fieldCls} w-32`}
            />
            <select value={action} onChange={(e) => setAction(e.target.value)} className={fieldCls}>
              {DECISION_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={S.reasonPh}
            rows={2}
            className={`${fieldCls} w-full resize-y`}
          />
          <button
            onClick={() => void submit()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            <Gavel size={14} />
            {S.submit}
          </button>
        </div>
        <p className="px-1 text-xs font-medium text-tertiary">{S.recentDecisions}</p>
        <DataTable<DecisionRow>
          screenId="A6.19"
          permission={PERMISSIONS.USERS_VIEW}
          columns={columns}
          fetcher={fetcher}
          rowId={(r) => String(r.id)}
          exportable
          emptyState={{ icon: <Gavel size={28} strokeWidth={1.5} /> }}
        />
      </div>
    </>
  )
}

/**
 * A6.14 — Ikkinchi ko'rib chiqish (appeal / second-review) navbati. Qayta ko'rib
 * chiqishga yuborilgan qarorlar (GET /admin/moderation/review-queue).
 */
export function SecondReviewQueueScreen() {
  const columns = useMemo<AdminColumn<ReviewRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id' },
      { key: 'target_type', header: S.targetType, type: 'text' },
      { key: 'target_id', header: S.targetId, type: 'number' },
      { key: 'category', header: S.category, type: 'text' },
      { key: 'first_decision', header: S.firstDecision, type: 'text' },
      { key: 'requested_by', header: S.requestedBy, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'created_at', header: S.created, type: 'date' },
    ],
    [],
  )
  return (
    <ModListScreen<ReviewRow>
      screenId="A6.14"
      title={S.review}
      description={S.reviewDesc}
      columns={columns}
      fetcher={makeFetcher<ReviewRow>(
        '/admin/moderation/review-queue',
        'items',
        (r) => `${r.category} ${r.first_decision} ${r.requested_by}`,
      )}
      rowId={(r) => String(r.id)}
      icon={<ClipboardCheck size={28} strokeWidth={1.5} />}
    />
  )
}

/**
 * A6.22 — Ikkinchi fikr so'rash. Moderator qiyin holatda hamkasbdan fikr so'raydi
 * (GET /admin/moderation/second-opinion). Qator amali: fikr berish
 * (POST /admin/moderation/second-opinion/:id/opinion).
 */
export function SecondOpinionScreen() {
  const { toast } = useToast()
  const columns = useMemo<AdminColumn<SecondOpinionRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id' },
      { key: 'target_type', header: S.targetType, type: 'text' },
      { key: 'target_id', header: S.targetId, type: 'number' },
      { key: 'category', header: S.category, type: 'text' },
      { key: 'asked_by', header: S.askedBy, type: 'text' },
      { key: 'question', header: S.question, type: 'longtext' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'created_at', header: S.created, type: 'date' },
    ],
    [],
  )
  const opine = useCallback(
    async (row: SecondOpinionRow, opinion: 'agree' | 'disagree') => {
      const note = (window.prompt(S.reason) ?? '').trim()
      try {
        await giveSecondOpinion(row.id, opinion, note)
        toast(S.opinionSaved, { tone: 'success' })
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )
  const rowActions = useMemo<RowAction<SecondOpinionRow>[]>(
    () => [
      { id: 'agree', label: S.agree, onSelect: (r) => void opine(r, 'agree') },
      { id: 'disagree', label: S.disagree, onSelect: (r) => void opine(r, 'disagree'), danger: true },
    ],
    [opine],
  )
  return (
    <ModListScreen<SecondOpinionRow>
      screenId="A6.22"
      title={S.secondOpinion}
      description={S.secondOpinionDesc}
      columns={columns}
      fetcher={makeFetcher<SecondOpinionRow>(
        '/admin/moderation/second-opinion',
        'items',
        (r) => `${r.category} ${r.asked_by} ${r.question}`,
      )}
      rowId={(r) => String(r.id)}
      icon={<Users size={28} strokeWidth={1.5} />}
      rowActions={rowActions}
    />
  )
}
